import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import CssBaseline from '@material-ui/core/CssBaseline';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import classNames from 'classnames';
import { Checkbox, FormControlLabel, Link } from '@material-ui/core';
import { Text } from 'recharts';
import PublicHeader from '../../components/PublicHeader';
import {
  isAuthenticated,
  sendLogin,
  changePassword,
  signIn,
  getCurrentUser,
  setPreferredMFA,
  updatePhone,
  verifyPhone,
  verifyPhoneSubmit
} from '../../auth/Auth';
import { getAccounts, editAccountStatus } from '../../actions/Accounts';
import ValidationText from '../../components/atoms/ValidationText';
import RequiredButton from '../../components/atoms/RequiredButton';
import ChangeButton from '../../components/atoms/ChangeButton';
import ProgressDialog from '../../components/molecules/ProgressMobileDialog';
import SendCodeButton from '../../components/atoms/SendCodeButton';

const styles = () => ({
  '@global': {
    body: {
      backgroundColor: '#F7F7F7',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
    }
  },
  main: {
    flexGrow: 1,
    maxWidth: 375,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 20
    // border: '1px solid #ccc'
  },
  page: {
    paddingLeft: 15,
    paddingRight: 15
  },
  paper: {
    display: 'flex',
    flexDirection: 'column'
  },
  titleGroup: {
    marginTop: 20,
    marginBottom: 20,
    display: 'flex',
    alignItems: 'flex-start'
  },
  pagerInDiv: {
    padding: '20px 15px'
  },
  linkToHome: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 20
  },
  noteText: {
    display: 'flex',
    fontSize: 12,
    paddingBottom: 20
  },
  caution: {
    color: '#ff0000',
    fontWeight: 500,
    fontSize: 12
  },
  textParagraphy: {
    display: 'flex',
    paddingBottom: 20
  },
  btnSendCode: {
    textAlign: 'right',
    paddingBottom: 20
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold'
  },
  policyText: {
    fontSize: 12,
    fontWeight: 700
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators({ getAccounts, editAccountStatus }, dispatch)
    })
  ),
  setPropTypes(propTypes),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isRedirect', 'updateIsRedirect', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('oldPassword', 'setOldPassword', ''),
  withState('oldPasswordError', 'setOldPasswordError', ''),
  withState('password', 'setPassword', ''),
  withState('passwordError', 'setPasswordError', ''),
  withState('confirmPassword', 'setConfirmPassword', ''),
  withState('confirmPasswordError', 'setConfirmPasswordError', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneError', 'setPhoneError', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withState('phoneAuthCodeError', 'setPhoneAuthCodeError', ''),
  withState('cognitoUserId', 'setCognitoUserId', ''),
  withState('isSentCode', 'updateIsSentCode', false),
  withState('btnConfirmText', 'updateBtnConfirmText', 'OK'),
  withState('titleConfirmText', 'updateTitleConfirmText', null),
  withState('checkPolicy', 'setCheckPolicy', false),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleRedirect,
    loading,
    updateLoading,
    isRedirect,
    updateIsRedirect,
    openProgress,
    updateOpenProgress,
    resultMessage,
    setResultMessage,
    oldPassword,
    setOldPassword,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,
    phone,
    setPhone,
    phoneAuthCode,
    setPhoneAuthCode,
    account,
    setCognitoUserId,
    isSentCode,
    updateIsSentCode,
    btnConfirmText,
    updateBtnConfirmText,
    titleConfirmText,
    updateTitleConfirmText,
    oldPasswordError,
    setOldPasswordError,
    phoneError,
    setPhoneError,
    phoneAuthCodeError,
    setPhoneAuthCodeError,
    checkPolicy,
    setCheckPolicy
  }) => {
    // Disable auth
    const isPhoneVerificationRequired = false;

    const isGroup = typeof account !== 'undefined' && account.role === 'GROUP';
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isFukuoka =
      typeof account !== 'undefined' && account.fukuokaFlag === 1;

    const errors = {
      passwordError,
      confirmPasswordError,
      oldPasswordError,
      phoneError,
      phoneAuthCodeError
    };
    const validPassword = () => {
      const value = password;
      if (value === '') {
        setPasswordError('必須項目です。');
        return 1;
      }
      if (
        value.length < 8 ||
        !/[A-Z]/.test(value) ||
        !/[a-z]/.test(value) ||
        !/[0-9]/.test(value)
      ) {
        setPasswordError(
          '英大文字・英小文字・数字全てを含め、8文字以上で入力してください。'
        );
        return 1;
      }
      setPasswordError('');
      setPassword(value);
      return 0;
    };
    const validConfirmPassword = () => {
      const value = password;
      const valueConfirm = confirmPassword;
      if (valueConfirm === '') {
        setConfirmPasswordError('必須項目です。');
        return 1;
      }
      if (value !== valueConfirm) {
        setConfirmPasswordError('パスワードが一致していません。');
        return 1;
      }
      setConfirmPasswordError('');
      setConfirmPassword(value);
      return 0;
    };

    const validOldPassword = () => {
      if (oldPassword === '') {
        setOldPasswordError('必須項目です。');
        return 1;
      }
      setOldPasswordError('');
      return 0;
    };

    const validPhone = () => {
      const value = phone.trim();
      if (value === '') {
        setPhoneError('必須項目です。');
        return 1;
      }
      setPhoneError('');
      setPhone(value);
      return 0;
    };

    const validPhoneAuthCode = () => {
      const value = phoneAuthCode;
      if (isPhoneVerificationRequired && value === '') {
        setPhoneAuthCodeError('必須項目です。');
        return 1;
      }
      setPhoneAuthCodeError('');
      return 0;
    };

    const validStep1 = () => {
      const errorCount =
        validPassword() +
        validConfirmPassword() +
        validPhone() +
        validOldPassword();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const valid = () => {
      const errorCount =
        validPhoneAuthCode() +
        validPassword() +
        validConfirmPassword() +
        validOldPassword();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const sendCodeAction = async () => {
      if (!validStep1()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        signIn(account.email, oldPassword)
          .then(signInUser => {
            setCognitoUserId(signInUser.getUsername());
            getCurrentUser()
              .then(async user => {
                const phoneWithCountry = `+81${phone
                  .replace(/-/g, '')
                  .slice(1)}`;
                await setPreferredMFA(user, 'NOMFA');
                await updatePhone(user, phoneWithCountry);
                await verifyPhone(user);
                setResultMessage('認証コードを送信しました。');
                updateLoading(false);
                updateIsSentCode(true);
              })
              .catch(() => {
                setResultMessage('システムエラーが発生しました。');
                updateLoading(false);
              });
          })
          .catch(() => {
            setResultMessage('システムエラーが発生しました。');
            updateLoading(false);
          });
      }
    };

    const changeAction = async () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        const accountData = await actions
          .getAccounts({ cognitoUserId: '' })
          .then(data => checkSession(data));
        getCurrentUser()
          .then(async user => {
            if (isPhoneVerificationRequired) {
              await verifyPhoneSubmit(user, phoneAuthCode);
            }
            await changePassword(oldPassword, password)
              .then(async () => {
                const params = accountData.payload;
                params.status = 'AVAILABLE';
                const editedStatus = await actions.editAccountStatus(
                  params.manageAccountId,
                  params
                );
                if (editedStatus.type === 'EDIT_ACCOUNT_STATUS_SUCCESS') {
                  updateIsRedirect(true);
                  setResultMessage(
                    isFukuoka
                      ? 'パスワードの変更が完了しました。続いて教室情報を編集することができます。（任意）'
                      : 'パスワードの変更が完了しました。続いて審査申請情報の入力をお願いいたします。'
                  );
                  updateLoading(false);
                  updateBtnConfirmText(
                    isFukuoka ? '教室情報編集へ進む' : '審査情報を入力する'
                  );
                  updateTitleConfirmText('パスワード変更完了');
                } else {
                  setResultMessage('パスワード変更に失敗しました。');
                  updateLoading(false);
                }
              })
              .catch(() => {
                setResultMessage('パスワード変更に失敗しました。');
                updateLoading(false);
              });
          })
          .catch(() => {
            setResultMessage('システムエラーが発生しました。');
            updateLoading(false);
          });
      }
    };

    const progressOkAction = () => {
      if (isRedirect) {
        updateOpenProgress(false);
        if (isGroup) {
          if (
            account.groupAccountStatus != null &&
            account.groupAccountStatus.status
          ) {
            handleRedirect('/home_group');
          } else {
            handleRedirect('/release_group');
          }
        } else if (isShop) {
          if (account.accountStatus != null && account.accountStatus.status) {
            handleRedirect('/home');
          } else {
            handleRedirect('/release_step');
          }
        } else {
          handleRedirect('/');
        }
      } else {
        updateOpenProgress(false);
      }
    };

    return (
      <Container component="main" className={classes.main}>
        <CssBaseline />
        <PublicHeader />
        <div className={classes.page}>
          <div className={classes.titleGroup}>
            <Typography component="h2" className={classes.pageTitle}>
              {!isSentCode ? 'パスワード変更' : '２段階認証'}
            </Typography>
          </div>
          {!isSentCode ? (
            <Paper className={classes.paper} elevation={1}>
              <div className={classes.pagerInDiv}>
                <Typography variant="p" className={classes.textParagraphy}>
                  お客様の情報の安全性を高めるために、パスワードをご自身で変更をお願いします。
                </Typography>
                <RequiredButton />
                <TextField
                  required
                  fullWidth
                  label="仮パスワード"
                  type="password"
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setOldPassword(e.target.value);
                  }}
                  onBlur={validOldPassword}
                />
                <ValidationText message={errors.oldPasswordError} />
                <RequiredButton />
                <TextField
                  required
                  fullWidth
                  label="新パスワード"
                  type="password"
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPassword(e.target.value);
                  }}
                  onBlur={validPassword}
                />
                <ValidationText message={errors.passwordError} />
                <Typography variant="p" className={classes.noteText}>
                  パスワードは英大文字・英小文字・数字の3種類全てを含め、8文字以上で設定してください。
                </Typography>
                <RequiredButton />
                <TextField
                  required
                  fullWidth
                  label="確認用パスワード"
                  type="password"
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setConfirmPassword(e.target.value);
                  }}
                  onBlur={validConfirmPassword}
                />
                <ValidationText message={errors.confirmPasswordError} />
              </div>
              {isPhoneVerificationRequired && (
                <>
                  <Divider />
                  <div className={classes.pagerInDiv}>
                    <Typography variant="p" className={classes.textParagraphy}>
                      SMS認証を行いますので、認証コードが送付可能な携帯電話番号をご入力ください。
                      <br />
                      携帯電話番号入力後、認証コードを送信し、SMSで届いた認証コードをご入力ください。
                    </Typography>
                    <RequiredButton />
                    <TextField
                      required
                      label="電話番号"
                      fullWidth
                      className={classes.textField}
                      margin="normal"
                      onChange={e => {
                        setPhone(e.target.value);
                      }}
                      onBlur={validPhone}
                    />
                    <ValidationText message={errors.phoneError} />
                    <Typography variant="p" className={classes.caution}>
                      ※SMS認証コードが届くまでに時間がかかる場合があります。
                    </Typography>
                  </div>
                </>
              )}
              <Divider />
              <div className={classes.pagerInDiv}>
                <Typography variant="p">
                  ご利用にあたっては、
                  <Link
                    href="https://gochimeshi.com/policy/owner-terms/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    加盟店規約
                  </Link>
                  ・
                  <Link
                    href="https://gochimeshi.com/policy/privacy/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    プライバシーポリシー
                  </Link>
                  をご参照、ご同意いただけますようお願いいたします。
                </Typography>
                <br />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkPolicy}
                      onChange={e => {
                        setCheckPolicy(e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Text className={classes.policyText}>
                      加盟店規約・プライバシーポリシーに同意する
                    </Text>
                  }
                />
              </div>
              <Divider />
              <div className={classes.pagerInDiv}>
                <SendCodeButton
                  textButton={isPhoneVerificationRequired ? '送信' : '変更する'}
                  fullWidth
                  onClickAction={
                    isPhoneVerificationRequired ? sendCodeAction : changeAction
                  }
                  enabled={checkPolicy}
                />
              </div>
            </Paper>
          ) : (
            <Paper className={classes.paper} elevation={1}>
              <div className={classes.pagerInDiv}>
                <RequiredButton />
                <TextField
                  required
                  label="認証コード"
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPhoneAuthCode(e.target.value);
                  }}
                  onBlur={validPhoneAuthCode}
                />
                <ValidationText message={errors.phoneAuthCodeError} />
              </div>
              <Divider />
              <div className={classes.pagerInDiv}>
                <Typography variant="p" className={classes.textParagraphy}>
                  認証コードが届かない場合は再送信をお願いします。
                </Typography>
                <RequiredButton />
                <TextField
                  // required
                  label="電話番号"
                  fullWidth
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setPhone(e.target.value);
                  }}
                  onBlur={validPhone}
                />
                <div className={classes.btnSendCode}>
                  <SendCodeButton
                    textButton="認証コードを再送信"
                    onClickAction={sendCodeAction}
                  />
                </div>

                <Typography variant="p" className={classes.caution}>
                  ※SMS認証コードが届くまでに時間がかかる場合があります。
                  <br />
                  SMSの認証コード送信ボタンを２回押すと必ずエラーとなり進めなくなりますので、そのままお待ちください。
                </Typography>
              </div>
              <Divider />
              <div
                className={classNames(
                  classes.pagerInDiv,
                  classes.textParagraphy
                )}
              >
                <ChangeButton
                  fullWidth
                  onClickAction={changeAction}
                  btnText="確定"
                />
              </div>
            </Paper>
          )}
        </div>

        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
          title={titleConfirmText}
          btnText={btnConfirmText}
        />
      </Container>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
